import { useRecoilValue } from 'recoil';
import Crosshairs from './Crosshairs';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { usePlaneRedrawTick } from '~/hooks/viewer/annotationOverlay';
import { viewManagerState } from '~/state/atoms/engineAtoms';

export interface CrosshairsComponentProps {
    view: PlaneView | null;
}

// Only the intersection event, not the hook's default set. The crosshair
// position is a scroll LEVEL, a percent of the canvas rect, so a zoom, a fit
// or a resize moves the lines too, and the owner must re-render this
// component on each of them.
const REDRAW_EVENTS: readonly string[] = ['planeIntersectionPointChanged'];

const CrosshairsComponent = ({ view }: CrosshairsComponentProps) => {
    const viewManager = useRecoilValue(viewManagerState);

    // Re-render when the plane intersection point changes (e.g. mouse wheel
    // scroll in another view), so the lines follow a move the pointer over
    // this view did not make. The tick value itself is unused: the
    // re-render alone re-reads the view's scroll levels below.
    usePlaneRedrawTick(viewManager, REDRAW_EVENTS);

    if (!view) {
        return null;
    }

    // Out-of-field positions (outside 0..100%) are handled inside the
    // component per axis; 0% is a valid position, so no truthiness checks.
    const scrollLevel = view.planeIntersectionPointToScrollLevel();

    const handleCrosshairChange = (
        type: 'horizontal' | 'vertical',
        delta: number
    ) => {
        view.scrollLevelToPlaneIntersectionPoint(type, delta);
    };

    return (
        <Crosshairs
            view={view}
            vertical={scrollLevel.vertical}
            horizontal={scrollLevel.horizontal}
            handleCrosshairChange={handleCrosshairChange}
        />
    );
};

export default CrosshairsComponent;
